import { systemText, type Channel, type Me, type Message, type SystemEvent } from "@novachat/shared";
import {
  Archive,
  ArchiveRestore,
  Crown,
  LogIn,
  LogOut,
  Monitor,
  Phone,
  PhoneOff,
  UserMinus,
  UserPlus,
  CornerUpLeft,
  MessagesSquare,
  Pencil,
  Pin,
  PinOff,
  SmilePlus,
  Trash2,
} from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useDirectory } from "../lib/directory";
import { errorMessage } from "../lib/errors";
import { plainText, toEditable, toWire } from "../lib/mentions";
import { clock, dayLabel, fullDate, sameDay } from "../lib/time";
import {
  discardMessage,
  editMessage,
  pinMessage,
  retryMessage,
  toggleReaction,
  useChat,
  type PendingMessage,
} from "../state/chat";
import { Attachments, LinkCards } from "./Attachments";
import { JiraCards } from "./JiraCards";
import { Avatar } from "./Avatar";
import { ProfileButton } from "./ProfileCard";
import { Markdown } from "./Markdown";
import { useDismiss } from "../lib/dismiss";

export const QUICK_REACTIONS = [
  "👍",
  "❤️",
  "😂",
  "🎉",
  "😮",
  "😢",
  "🙏",
  "🔥",
  "✅",
  "👀",
];
const GROUP_WITHIN_MS = 5 * 60_000;
export interface MessageRowProps {
  channel: Channel;
  me: Me;
  message: Message;
  /** Set while the message waits in the outbox. */
  pending?: PendingMessage | undefined;
  previous: Message | null;
  /** Draws the "Yeni mesajlar" line above this message. */
  firstUnread: boolean;
  active: boolean;
  editing: boolean;
  highlighted: boolean;
  onActivate: () => void;
  onReply: () => void;
  onEdit: () => void;
  onEditDone: () => void;
  onDelete: () => void;
  onJump: (messageId: string) => void;
  /** In a channel's history: opens this message's thread ("Dalda yanıtla"). Missing inside a thread. */
  onThread?: (() => void) | undefined;
}

export function MessageRow(props: MessageRowProps) {
  if (props.message.kind === "system") return <SystemRow {...props} />;
  return <UserRow {...props} />;
}

const SYSTEM_ICONS: Record<SystemEvent["type"], typeof Phone> = {
  "call.started": Phone,
  "call.ended": PhoneOff,
  "screen.started": Monitor,
  "member.added": UserPlus,
  "member.left": LogOut,
  "member.removed": UserMinus,
  "member.joined": LogIn,
  "channel.renamed": Pencil,
  "channel.topic": Pencil,
  "channel.archived": Archive,
  "channel.restored": ArchiveRestore,
  "owner.granted": Crown,
  "owner.revoked": Crown,
  "owner.handed": Crown,
};

/** Something that happened ("Selçuk görüşme başlattı"): a quiet line with an icon, no actions. */
function SystemRow({ message, previous, firstUnread }: MessageRowProps) {
  const newDay = !previous || !sameDay(previous.createdAt, message.createdAt);
  // A line from a newer server (a type this app does not know yet): its stored text, a plain icon.
  const Icon = (message.event && SYSTEM_ICONS[message.event.type]) ?? Pencil;
  const text = (message.event && systemText(message.event)) || message.content;
  const call = message.event?.type === "call.started";
  return (
    <div className="pb-px">
      {newDay && <Divider label={dayLabel(message.createdAt)} />}
      {firstUnread && <Divider label="Yeni mesajlar" accent />}
      <p className="m-0 flex items-center gap-2.5 px-3 py-1 text-[13px] text-fg-3 md:px-5">
        <span className={`grid size-6 flex-none place-items-center rounded-full ${call ? "bg-green-400/15 text-green-400" : "bg-surface-2 text-fg-3"}`} aria-hidden="true">
          <Icon size={13} />
        </span>
        <span className="min-w-0 flex-1">{text}</span>
        <time dateTime={message.createdAt} title={fullDate(message.createdAt)} className="flex-none text-[11px] text-fg-3">
          {clock(message.createdAt)}
        </time>
      </p>
    </div>
  );
}

function UserRow(props: MessageRowProps) {
  const { message, previous, pending, firstUnread, me } = props;
  const newDay = !previous || !sameDay(previous.createdAt, message.createdAt);
  const grouped =
    !newDay &&
    !firstUnread &&
    !message.replyTo &&
    previous!.kind !== "system" &&
    previous!.author?.id === message.author?.id &&
    !previous!.deletedAt &&
    Date.parse(message.createdAt) - Date.parse(previous!.createdAt) <
      GROUP_WITHIN_MS;
  const author = message.author ?? {
    id: "unknown",
    name: "Silinmiş kullanıcı",
  };
  const mine = message.author?.id === me.id;
  const reactions = message.reactions;
  const people = useDirectory();
  // Mentions of me stand out (design D): a warm tint and an orange edge.
  const mentionsMe =
    !mine &&
    !message.deletedAt &&
    (message.mentions.everyone || message.mentions.userIds.includes(me.id));

  return (
    <div className="pb-px">
      {newDay && <Divider label={dayLabel(message.createdAt)} />}
      {firstUnread && <Divider label="Yeni mesajlar" accent />}
      <article
        aria-label={`${author.name}, ${clock(message.createdAt)}`}
        onClick={(event) => {
          if (!(event.target as HTMLElement).closest("a, button, textarea"))
            props.onActivate();
        }}
        className={`group relative flex gap-3 px-3 md:px-5 ${grouped ? "py-0.5" : "mt-1.5 pt-1.5 pb-0.5"} ${
          props.highlighted
            ? "bg-brand-500/15"
            : mentionsMe
              ? "bg-brand-500/9 shadow-[inset_2px_0_0_var(--color-brand-400)]"
              : props.active
                ? "bg-surface-2/60"
                : "hover:bg-surface-2/40"
        } transition-colors duration-500 ${pending ? "opacity-60" : ""}`}
      >
        {grouped ? (
          <time
            dateTime={message.createdAt}
            title={fullDate(message.createdAt)}
            className="w-[38px] flex-none pt-[3px] text-right text-[11px] leading-5 text-fg-3 opacity-0 group-hover:opacity-100"
          >
            {clock(message.createdAt)}
          </time>
        ) : message.author ? (
          <ProfileButton
            user={author}
            label={`${author.name} profili`}
            className="mt-0.5 h-fit flex-none rounded-full"
          >
            <Avatar id={author.id} name={author.name} size={38} />
          </ProfileButton>
        ) : (
          <Avatar
            id={author.id}
            name={author.name}
            size={38}
            className="mt-0.5"
          />
        )}

        <div className="min-w-0 flex-1">
          {!grouped && message.replyTo && (
            <button
              type="button"
              onClick={() => props.onJump(message.replyTo!.id)}
              className="mb-1 flex max-w-full min-w-0 items-center gap-1.5 border-0 bg-transparent p-0 text-left text-[12.5px] text-fg-3 hover:text-fg-2"
            >
              <CornerUpLeft
                size={14}
                className="flex-none"
                aria-hidden="true"
              />
              <span className="flex-none font-semibold text-fg-2">
                {message.replyTo.author?.name ?? "Silinmiş kullanıcı"}
              </span>
              <span className="truncate">
                {message.replyTo.deleted
                  ? "Bu mesaj silindi"
                  : plainText(message.replyTo.content, people) || "Dosya eki"}
              </span>
            </button>
          )}
          {!grouped && (
            <div className="flex items-baseline gap-2">
              {message.author ? (
                <ProfileButton
                  user={author}
                  className="min-w-0 truncate text-[15px] font-bold hover:underline"
                >
                  {author.name}
                </ProfileButton>
              ) : (
                <span className="truncate text-[15px] font-bold">
                  {author.name}
                </span>
              )}
              <time
                dateTime={message.createdAt}
                title={fullDate(message.createdAt)}
                className="flex-none text-xs text-fg-3"
              >
                {clock(message.createdAt)}
              </time>
            </div>
          )}

          {props.editing ? (
            <EditBox message={message} onDone={props.onEditDone} />
          ) : message.deletedAt ? (
            <p className="m-0 text-sm text-fg-3 italic">Bu mesaj silindi</p>
          ) : message.content === "" && !message.editedAt ? null : (
            <Markdown
              content={message.content}
              everyone={message.mentions.everyone}
              {...(message.jira?.length ? { jira: message.jira } : {})}
              suffix={
                message.editedAt && (
                  <span
                    title={fullDate(message.editedAt)}
                    className="ml-1.5 text-[11px] text-fg-3"
                  >
                    (düzenlendi)
                  </span>
                )
              }
            />
          )}

          {!message.deletedAt && <Attachments items={message.attachments} />}
          {!message.deletedAt && <LinkCards links={message.links} />}
          {!message.deletedAt && <JiraCards cards={message.jira} />}
          {pending && <PendingStatus pending={pending} />}

          {reactions.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {reactions.map((reaction) => {
                const reacted = reaction.userIds.includes(me.id);
                return (
                  <button
                    key={reaction.emoji}
                    type="button"
                    aria-pressed={reacted}
                    aria-label={`${reaction.emoji} ${reaction.userIds.length} kişi${reacted ? ", sen dahil" : ""}`}
                    onClick={() => toggleReaction(message, reaction.emoji)}
                    className={`nc-press flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[13px] ${
                      reacted
                        ? "border-brand-400/55 bg-brand-500/16 text-fg-1"
                        : "border-line bg-surface-2 text-fg-2"
                    }`}
                  >
                    {reaction.emoji}
                    <span className="font-bold">{reaction.userIds.length}</span>
                  </button>
                );
              })}
            </div>
          )}
          {props.onThread && message.thread && (
            <ThreadLine message={message} onOpen={props.onThread} />
          )}
        </div>

        {!pending && !message.deletedAt && !props.editing && (
          <Toolbar {...props} mine={mine} />
        )}
      </article>
    </div>
  );
}

/** Under a message with replies: "3 yanıt · Ayşe, Burak · son 10:31", or "2 yeni yanıt" in a thread you follow. */
function ThreadLine({ message, onOpen }: { message: Message; onOpen: () => void }) {
  const thread = message.thread!;
  const fresh = useChat((s) => s.followed[message.id]?.unread ?? 0);
  const names = thread.people.map((p) => p.name.split(" ")[0]).join(", ");
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Konu dalı: ${thread.count} yanıt${fresh > 0 ? `, ${fresh} yeni` : ""}, son ${clock(thread.lastReplyAt)}`}
      className="nc-press mt-1.5 flex max-w-full items-center gap-2 rounded-md border border-line bg-surface-2/60 px-2 py-1 text-left text-[13px] hover:border-line-strong hover:bg-surface-2"
    >
      <span className="flex flex-none -space-x-1.5" aria-hidden="true">
        {thread.people.slice(0, 3).map((p) => (
          <Avatar key={p.id} id={p.id} name={p.name} size={20} />
        ))}
      </span>
      <span className={`flex-none font-bold ${fresh > 0 ? "text-brand-400" : "text-fg-1"}`}>
        {fresh > 0 ? `${fresh} yeni yanıt` : `${thread.count} yanıt`}
      </span>
      <span className="min-w-0 truncate text-fg-3">
        {names && `${names} · `}son {clock(thread.lastReplyAt)}
      </span>
    </button>
  );
}

function Divider({
  label,
  accent = false,
}: {
  label: string;
  accent?: boolean;
}) {
  return (
    <div
      role="separator"
      className={`flex items-center gap-3 px-4 py-1.5 text-xs md:px-5 ${accent ? "font-bold text-brand-400" : "font-semibold text-fg-3"}`}
    >
      <span
        className={`h-px flex-1 ${accent ? "bg-brand-400/45" : "bg-line"}`}
      />
      {label}
      {!accent && <span className="h-px flex-1 bg-line" />}
    </div>
  );
}

function PendingStatus({ pending }: { pending: PendingMessage }) {
  if (!pending.failed)
    return <p className="m-0 mt-0.5 text-xs text-fg-3">Gönderiliyor…</p>;
  return (
    <p className="m-0 mt-1 flex flex-wrap items-center gap-x-3 text-xs text-[#EE9191]">
      Gönderilemedi.
      <button
        type="button"
        onClick={() => retryMessage(pending.clientId)}
        className="border-0 bg-transparent p-0 font-semibold text-fg-1 underline"
      >
        Tekrar dene
      </button>
      <button
        type="button"
        onClick={() => discardMessage(pending.clientId)}
        className="border-0 bg-transparent p-0 font-semibold text-fg-2 underline"
      >
        Sil
      </button>
    </p>
  );
}

/** Hover (or tap, on phones) shows the actions; the emoji button opens a short picker. */
function Toolbar({
  channel,
  message,
  mine,
  active,
  onReply,
  onEdit,
  onDelete,
  onThread,
}: MessageRowProps & { mine: boolean }) {
  // Only what the reader's roles in this channel allow (docs/plans/2026-11-03-channel-roles-design.md).
  const can = (permission: Channel["permissions"][number]) =>
    channel.permissions.includes(permission);
  const [picking, setPicking] = useState(false);
  const bar = useRef<HTMLDivElement>(null);
  useDismiss(picking, () => setPicking(false), bar);
  useEffect(() => {
    if (!active) setPicking(false);
  }, [active]);
  const button =
    "grid size-8 place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-3 hover:text-fg-1";
  return (
    <div
      ref={bar}
      className={`absolute -top-3.5 right-3 z-10 flex-col items-end gap-1 md:right-5 ${active ? "flex" : "hidden group-focus-within:flex group-hover:flex"}`}
    >
      <div
        role="toolbar"
        aria-label="Mesaj işlemleri"
        className="flex rounded-md border border-line-strong bg-surface-2 p-0.5 shadow-lg"
      >
        {can("react") && (
          <button
            type="button"
            aria-label="Tepki ekle"
            aria-expanded={picking}
            title="Tepki ekle"
            onClick={() => setPicking((p) => !p)}
            className={button}
          >
            <SmilePlus size={16} />
          </button>
        )}
        {can("write") && (
          <button
            type="button"
            aria-label="Yanıtla"
            title="Yanıtla"
            onClick={onReply}
            className={button}
          >
            <CornerUpLeft size={16} />
          </button>
        )}
        {onThread && can("write") && (
          <button
            type="button"
            aria-label="Dalda yanıtla"
            title="Dalda yanıtla"
            onClick={onThread}
            className={button}
          >
            <MessagesSquare size={16} />
          </button>
        )}
        {mine && (
          <button
            type="button"
            aria-label="Düzenle"
            title="Düzenle"
            onClick={onEdit}
            className={button}
          >
            <Pencil size={16} />
          </button>
        )}
        {can("pin") && !message.threadRootId && (
          <button
            type="button"
            aria-label={message.pinned ? "Sabitlemeyi kaldır" : "Sabitle"}
            title={message.pinned ? "Sabitlemeyi kaldır" : "Sabitle"}
            onClick={() =>
              void pinMessage(message.id, !message.pinned).catch(() => {})
            }
            className={button}
          >
            {message.pinned ? <PinOff size={16} /> : <Pin size={16} />}
          </button>
        )}
        {(mine || can("delete_others")) && (
          <button
            type="button"
            aria-label="Sil"
            title="Sil"
            onClick={onDelete}
            className={`${button} hover:text-[#EE9191]`}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
      {picking && (
        <div
          role="group"
          aria-label="Tepkiler"
          className="flex max-w-[calc(100vw-24px)] flex-wrap rounded-md border border-line-strong bg-surface-2 p-1 shadow-lg"
        >
          {QUICK_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              aria-label={emoji}
              onClick={() => {
                toggleReaction(message, emoji);
                setPicking(false);
              }}
              className="grid size-9 place-items-center rounded-sm border-0 bg-transparent text-lg hover:bg-surface-3"
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Enter saves, Escape cancels, Shift+Enter adds a line. */
function EditBox({
  message,
  onDone,
}: {
  message: Message;
  onDone: () => void;
}) {
  const people = useDirectory();
  const [text, setText] = useState(() => toEditable(message.content, people));
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [text]);

  async function save() {
    const content = toWire(text.trim(), people);
    if (!content || content === message.content) return onDone();
    try {
      await editMessage(message.id, content);
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      onDone();
    } else if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      void save();
    }
  }

  return (
    <div className="mt-1">
      <textarea
        ref={input}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        aria-label="Mesajı düzenle"
        maxLength={4000}
        rows={1}
        className="block w-full resize-none rounded-md border border-line-strong bg-surface-2 px-3 py-2 text-base leading-[22px] text-fg-1 focus:border-brand-400 focus:outline-none md:text-[15px]"
      />
      <p className="m-0 mt-1 text-xs text-fg-3">
        {error ? <span className="text-[#EE9191]">{error} </span> : null}
        <button
          type="button"
          onClick={onDone}
          className="border-0 bg-transparent p-0 text-xs font-semibold text-teal-400"
        >
          Vazgeç
        </button>
        {" · "}
        <button
          type="button"
          onClick={() => void save()}
          className="border-0 bg-transparent p-0 text-xs font-semibold text-teal-400"
        >
          Kaydet
        </button>
        <span className="max-md:hidden"> · Esc vazgeçer, Enter kaydeder</span>
      </p>
    </div>
  );
}
